/**
 * MessageThread.js - Conversation thread between parent and child
 *
 * Features:
 * - Real-time messages via Firestore onSnapshot
 * - Auto-scroll to bottom on new messages
 * - Timestamp formatting
 * - Read status tracking
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { Send, Lock, ExpandMore } from '@mui/icons-material';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  getDocs,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const PAGE_SIZE = 30;

function MessageThread({ familyId, otherUserId, otherUserName, isParentsOnly = false }) {
  const { currentUser, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [oldestVisible, setOldestVisible] = useState(null);
  const bottomRef = useRef(null);
  const listRef = useRef(null);

  const myId = currentUser?.uid;
  const myRole = userProfile?.role || 'child';
  const mySenderId = `${myRole}:${myId}`;
  const otherSenderId = otherUserId; // already 'parent:xxx' or 'child:xxx' format

  useEffect(() => {
    if (!familyId || !myId || !otherUserId) {
      setLoading(false);
      return;
    }

    // Listen for most recent PAGE_SIZE messages between these two users
    const messagesRef = collection(db, `families/${familyId}/messages`);
    const q = query(
      messagesRef,
      where('participants', 'array-contains', myId),
      orderBy('timestamp', 'desc'),
      limit(PAGE_SIZE + 1)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const allDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(m =>
          (m.fromId === myId && m.toId === otherUserId) ||
          (m.fromId === otherUserId && m.toId === myId)
        );
      
      // Check if there are more messages
      if (allDocs.length > PAGE_SIZE) {
        setHasMore(true);
        allDocs.pop(); // Remove the extra one
      } else {
        setHasMore(false);
      }

      // Reverse to chronological order
      const msgs = allDocs.reverse();
      setMessages(msgs);
      if (msgs.length > 0) setOldestVisible(msgs[0]);
      setLoading(false);

      // Mark messages from other person as read
      msgs.filter(m => m.fromId === otherUserId && !m.read).forEach(m => {
        updateDoc(doc(db, `families/${familyId}/messages/${m.id}`), { read: true }).catch(() => {});
      });
    }, (err) => {
      console.error('[MessageThread] Error:', err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [familyId, myId, otherUserId]);

  const loadOlderMessages = async () => {
    if (!hasMore || loadingMore || !oldestVisible) return;
    setLoadingMore(true);
    try {
      const messagesRef = collection(db, `families/${familyId}/messages`);
      const q = query(
        messagesRef,
        where('participants', 'array-contains', myId),
        orderBy('timestamp', 'desc'),
        startAfter(oldestVisible.timestamp),
        limit(PAGE_SIZE + 1)
      );
      const snap = await getDocs(q);
      const olderDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(m =>
          (m.fromId === myId && m.toId === otherUserId) ||
          (m.fromId === otherUserId && m.toId === myId)
        );
      
      if (olderDocs.length > PAGE_SIZE) {
        setHasMore(true);
        olderDocs.pop();
      } else {
        setHasMore(false);
      }
      
      const olderMsgs = olderDocs.reverse();
      setMessages(prev => [...olderMsgs, ...prev]);
      if (olderMsgs.length > 0) setOldestVisible(olderMsgs[0]);
    } catch (err) {
      console.error('[MessageThread] loadOlderMessages error:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);

    const messageText = newMessage.trim();
    
    // Optimistic update - show message immediately
    const tempMessage = {
      id: `temp-${Date.now()}`,
      from: mySenderId,
      to: otherSenderId,
      fromId: myId,
      toId: otherUserId,
      text: messageText,
      timestamp: { toDate: () => new Date() },
      read: true,
      participants: [myId, otherUserId],
      familyId,
      isTemp: true
    };

    // Add to UI immediately
    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');

    try {
      const messageData = {
        from: mySenderId,
        to: otherSenderId,
        fromId: myId,
        toId: otherUserId,
        text: messageText,
        timestamp: Timestamp.now(),
        read: false,
        participants: [myId, otherUserId],
        familyId,
      };

      await addDoc(collection(db, `families/${familyId}/messages`), messageData);
    } catch (err) {
      console.error('[MessageThread] Send error:', err);
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 300 }}>
      {/* Parents Only Indicator */}
      {isParentsOnly && (
        <Alert severity="warning" icon={<Lock />} sx={{ mx: 1, mt: 1, py: 0.5 }}>
          <Typography variant="caption" fontWeight="bold">
            👨‍👩 Parents Only — Children cannot see this conversation
          </Typography>
        </Alert>
      )}

      {/* Messages List */}
      <Box ref={listRef} sx={{ flex: 1, overflow: 'auto', p: 1, mb: 1 }}>
        {/* Load Older Messages */}
        {hasMore && (
          <Box display="flex" justifyContent="center" pb={1}>
            <Button
              size="small"
              startIcon={loadingMore ? <CircularProgress size={14} /> : <ExpandMore />}
              onClick={loadOlderMessages}
              disabled={loadingMore}
              variant="text"
              color="inherit"
              sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
            >
              {loadingMore ? 'Loading...' : 'Load older messages'}
            </Button>
          </Box>
        )}
        {messages.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No messages yet. Say hi! 👋
            </Typography>
          </Box>
        ) : (
          messages.map(msg => {
            const isMe = msg.fromId === myId;
            return (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent: isMe ? 'flex-end' : 'flex-start',
                  mb: 1,
                }}
              >
                {!isMe && (
                  <Avatar sx={{ width: 28, height: 28, mr: 1, mt: 0.5, fontSize: '0.75rem' }}>
                    {otherUserName?.charAt(0)}
                  </Avatar>
                )}
                <Box sx={{ maxWidth: '75%', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  <Paper
                    elevation={1}
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: isMe ? 'primary.main' : 'grey.100',
                      color: isMe ? 'white' : 'text.primary',
                      borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    }}
                  >
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{msg.text}</Typography>
                  </Paper>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(msg.timestamp)}
                    </Typography>
                    {isMe && msg.read && (
                      <Typography variant="caption" color="primary" sx={{ fontSize: '0.75rem' }}>✓✓</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Message Input */}
      <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: '1px solid #e0e0e0' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          multiline
          maxRows={3}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!newMessage.trim() || sending}
          sx={{ alignSelf: 'flex-end', minWidth: 44, minHeight: 48 }}
        >
          {sending ? <CircularProgress size={20} /> : <Send />}
        </IconButton>
      </Box>
    </Box>
  );
}

export default MessageThread;
